import React from 'react';
import { Globe, Layout, MessageSquare, Target, TrendingUp } from 'lucide-react';
import { GoogleAd } from '../types/dataTypes';

interface GoogleAdAnalysisSummaryProps {
  ads: GoogleAd[];
}

interface CompetitorSummary {
  name: string;
  regions: string[];
  imageTypes: string[];
}

// Enhanced theme detection with categorization
const extractThemes = (text: string): string[] => {
  const themes: Set<string> = new Set();
  if (!text) return [];

  const themePatterns = {
    'Data & Analytics': ['data', 'analytics', 'insight', 'metrics', 'dashboard'],
    'Cloud & Infrastructure': ['cloud', 'infrastructure', 'platform', 'saas'],
    'AI & Machine Learning': ['ai', 'machine learning', 'ml', 'intelligence'],
    'Security & Compliance': ['security', 'compliance', 'governance', 'privacy'],
    'Business Value': ['roi', 'cost', 'efficiency', 'savings', 'value'],
    'Enterprise Solutions': ['enterprise', 'business', 'corporate', 'organization'],
    'Digital Transformation': ['transform', 'digital', 'modernize', 'innovate'],
    'Integration': ['integrate', 'connect', 'api', 'ecosystem'],
    'Performance': ['performance', 'speed', 'reliable', 'fast'],
    'Support & Services': ['support', 'service', 'expert', 'professional']
  };

  const textLower = text.toLowerCase();
  
  Object.entries(themePatterns).forEach(([theme, keywords]) => {
    if (keywords.some(keyword => textLower.includes(keyword))) {
      themes.add(theme);
    }
  });

  return Array.from(themes);
};

// Analyze headline patterns
const analyzeHeadlinePatterns = (headline: string): string[] => {
  const patterns: string[] = [];
  const headlineLower = headline.toLowerCase();

  if (headline.includes('?')) patterns.push('Question-based');
  if (/\d+/.test(headline)) patterns.push('Numbers/Statistics');
  if (/free|download|guide|ebook|report/i.test(headline)) patterns.push('Resource Offer');
  if (/now|today|limited|exclusive/i.test(headline)) patterns.push('Urgency');
  if (/how|why|what|when/i.test(headline)) patterns.push('Educational');
  if (/you|your/i.test(headline)) patterns.push('Personal Appeal');
  if (/new|introducing|announcing/i.test(headline)) patterns.push('News/Announcement');
  if (/save|roi|cost|price/i.test(headline)) patterns.push('Value Proposition');
  
  return patterns;
};

// Analyze description patterns
const analyzeDescriptionPatterns = (description: string): string[] => {
  const patterns: string[] = [];
  const descLower = description.toLowerCase();

  if (/problem|challenge|struggle/i.test(descLower)) patterns.push('Problem-Solution');
  if (/case study|success|customer/i.test(descLower)) patterns.push('Social Proof');
  if (/learn|discover|find out/i.test(descLower)) patterns.push('Educational');
  if (/award|leader|best|top/i.test(descLower)) patterns.push('Authority');
  if (/save|roi|return|revenue/i.test(descLower)) patterns.push('ROI-focused');
  if (/easy|simple|quick/i.test(descLower)) patterns.push('Ease of Use');
  if (/secure|protect|privacy/i.test(descLower)) patterns.push('Security Focus');
  if (/integrate|compatible|work with/i.test(descLower)) patterns.push('Integration');
  
  return patterns;
};

// Analyze content type
const analyzeContentType = (headline: string, description: string): string => {
  const combined = (headline + ' ' + description).toLowerCase();
  
  if (/guide|ebook|whitepaper|report/i.test(combined)) return 'Educational Content';
  if (/demo|watch|see it|preview/i.test(combined)) return 'Product Demo';
  if (/trial|try|start|begin/i.test(combined)) return 'Free Trial';
  if (/contact|speak|talk|call/i.test(combined)) return 'Sales Contact';
  if (/webinar|event|workshop/i.test(combined)) return 'Event/Webinar';
  if (/case study|success story/i.test(combined)) return 'Case Study';
  
  return 'Other';
};

const GoogleAdAnalysisSummary: React.FC<GoogleAdAnalysisSummaryProps> = ({ ads }) => {
  // Enhanced ad analysis with more detailed insights
  const adAnalysis = React.useMemo(() => {
    // Group ads by competitor
    const competitorSummaries = new Map<string, CompetitorSummary>();
    
    let allThemes: string[] = [];
    let allHeadlinePatterns: string[] = [];
    let allDescriptionPatterns: string[] = [];
    let contentTypes: string[] = [];
    
    ads.forEach(ad => {
      // Competitor grouping
      if (!competitorSummaries.has(ad.advertiserName)) {
        competitorSummaries.set(ad.advertiserName, {
          name: ad.advertiserName,
          regions: [],
          imageTypes: []
        });
      }
      
      const summary = competitorSummaries.get(ad.advertiserName)!;
      if (ad.creative_region && !summary.regions.includes(ad.creative_region)) {
        summary.regions.push(ad.creative_region);
      }

      // Content analysis
      const headlineThemes = extractThemes(ad.headline);
      const descriptionThemes = extractThemes(ad.description);
      allThemes = [...allThemes, ...headlineThemes, ...descriptionThemes];
      
      allHeadlinePatterns = [...allHeadlinePatterns, ...analyzeHeadlinePatterns(ad.headline)];
      allDescriptionPatterns = [...allDescriptionPatterns, ...analyzeDescriptionPatterns(ad.description)];
      contentTypes.push(analyzeContentType(ad.headline, ad.description));
    });
    
    // Count frequencies
    const countFrequency = (arr: string[]) => {
      return arr.reduce((acc, item) => {
        acc[item] = (acc[item] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
    };

    const themeFrequency = countFrequency(allThemes);
    const headlinePatternFrequency = countFrequency(allHeadlinePatterns);
    const descriptionPatternFrequency = countFrequency(allDescriptionPatterns);
    const contentTypeFrequency = countFrequency(contentTypes);

    // Sort by frequency
    const sortByFrequency = (obj: Record<string, number>) => 
      Object.entries(obj)
        .sort(([,a], [,b]) => b - a)
        .map(([item]) => item);

    // Calculate averages
    const averageHeadlineLength = Math.round(
      ads.reduce((sum, ad) => sum + ad.headline.length, 0) / ads.length
    );
    const averageDescriptionLength = Math.round(
      ads.reduce((sum, ad) => sum + ad.description.length, 0) / ads.length
    );

    // Get all unique regions
    const allRegions = new Set(ads.map(ad => ad.creative_region).filter(Boolean));

    return {
      competitors: Array.from(competitorSummaries.values()),
      themes: sortByFrequency(themeFrequency),
      headlinePatterns: sortByFrequency(headlinePatternFrequency),
      descriptionPatterns: sortByFrequency(descriptionPatternFrequency),
      contentTypes: sortByFrequency(contentTypeFrequency),
      regions: Array.from(allRegions),
      totalAds: ads.length,
      averageHeadlineLength,
      averageDescriptionLength,
      advertisersCount: competitorSummaries.size
    };
  }, [ads]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mt-8">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        📈 Google Ads Analysis
      </h2>

      <div className="space-y-8">
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
          <div className="space-y-6">
            <div className="flex items-start">
              <MessageSquare className="w-5 h-5 text-blue-500 mt-1 mr-3" />
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300">Headline Patterns</h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  {adAnalysis.headlinePatterns.map((pattern, idx) => (
                    <span 
                      key={idx}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200"
                    >
                      {pattern}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <Layout className="w-5 h-5 text-blue-500 mt-1 mr-3" />
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300">Description Patterns</h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  {adAnalysis.descriptionPatterns.map((pattern, idx) => (
                    <span 
                      key={idx}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                    >
                      {pattern}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <Target className="w-5 h-5 text-blue-500 mt-1 mr-3" />
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300">Content Types</h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  {adAnalysis.contentTypes.map((type, idx) => (
                    <span 
                      key={idx}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <Globe className="w-5 h-5 text-blue-500 mt-1 mr-3" />
              <div>
                <h4 className="font-medium text-gray-700 dark:text-gray-300">Geographic Focus</h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  {adAnalysis.regions.length > 0 ? adAnalysis.regions.map((region, idx) => (
                    <span 
                      key={idx}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200"
                    >
                      {region}
                    </span>
                  )) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">Global/Not specified</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            💡 Key Insights
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-6">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-4">Content Strategy</h4>
              <ul className="space-y-2">
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Average headline: {adAnalysis.averageHeadlineLength} chars
                </li>
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Average description: {adAnalysis.averageDescriptionLength} chars
                </li>
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Primary theme: {adAnalysis.themes[0] || 'N/A'}
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-6">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-4">Campaign Analysis</h4>
              <ul className="space-y-2">
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 mt-1.5"></span>
                  {adAnalysis.totalAds} active ads across {adAnalysis.advertisersCount} advertisers
                </li>
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 mt-1.5"></span>
                  Most common content: {adAnalysis.contentTypes[0]}
                </li>
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 mt-1.5"></span>
                  Primary pattern: {adAnalysis.headlinePatterns[0]}
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-6">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-4">Recommendations</h4>
              <ul className="space-y-2">
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 mt-1.5"></span>
                  Test {adAnalysis.headlinePatterns.slice(-1)[0]} headlines
                </li>
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 mt-1.5"></span>
                  Explore {adAnalysis.contentTypes.slice(-1)[0]} content
                </li>
                <li className="text-blue-800 dark:text-blue-200 text-sm flex items-start">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 mt-1.5"></span>
                  Add {adAnalysis.descriptionPatterns.slice(-1)[0]} messaging
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleAdAnalysisSummary;